/**
 * „Plattform → Mandanten" — die Betreiber-Ebene ueber allen Mandanten.
 *
 * Diese Seite schliesst den ersten Schritt der Onboarding-Kette: bis hierher
 * gab es `POST /api/v1/tenants`, aber keine Oberflaeche dazu — Mandanten kamen
 * nur aus dem Seed.
 *
 * Drei Dinge, die hier zusammengehoeren:
 *
 *   1. **Anlegen macht den Anlegenden zum Mitglied.** Die API setzt das in
 *      derselben Transaktion (`createTenant`, `ownerUserId`). Ohne das haette
 *      man einen Mandanten, den man nicht betreten kann: die Mandantenwahl der
 *      Oberflaeche liest ausschliesslich `GET /v1/me` → `memberships`.
 *      Deshalb laedt die Seite nach dem Anlegen die Sitzung neu, schaltet den
 *      aktiven Mandanten um und geht direkt weiter zu „Queues" — dem
 *      naechsten Schritt der Kette.
 *   2. **Kein Loeschen.** `audit_log` haengt mit `ON DELETE RESTRICT` am
 *      Mandanten (bewusst, siehe TODO.md) — ein Knopf, der immer fehlschlaegt,
 *      waere schlechter als ein Satz, der erklaert warum.
 *   3. **Sichtbarkeit ist Bequemlichkeit, nicht Schutz.** Wie bei
 *      `PlatformMailPage` prueft die API jede Route noch einmal
 *      (`requirePlatformAdmin` bzw. `tenant.manage`).
 */
import {
  createTenantSchema,
  LOCALES,
  updateTenantSchema,
  type Locale,
  type PlatformTenant,
} from '@tickethub/shared';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { membersApi, platformApi, tenantApi } from '@/lib/tickets';

/** Deutsche Umlaute vor der Normalisierung — sonst wird „Müller" zu „mller". */
const TRANSLITERATION: Record<string, string> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  ß: 'ss',
};

/**
 * Name → Slug-Vorschlag. Nur ein Vorschlag: das Feld bleibt editierbar, und
 * die Wahrheit ist `slugSchema` in `@tickethub/shared`.
 */
export function slugFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[äöüß]/g, (char) => TRANSLITERATION[char] ?? char)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 64)
    .replace(/^-+|-+$/g, '');
}

type TenantForm = { name: string; slug: string; locale: Locale };

const EMPTY_FORM: TenantForm = { name: '', slug: '', locale: 'de' };

export function PlatformTenantsPage() {
  const { t, i18n } = useTranslation();
  const { user, setActiveTenant, reload: reloadSession } = useSession();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState<PlatformTenant[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<TenantForm>(EMPTY_FORM);
  /** Hat jemand den Slug von Hand angefasst? Dann nicht mehr ueberschreiben. */
  const [slugTouched, setSlugTouched] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<TenantForm>(EMPTY_FORM);
  const [editError, setEditError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isPlatformAdmin = user?.is_platform_admin === true;

  const load = useCallback(async () => {
    try {
      setTenants((await platformApi.tenants()).items);
    } catch (error) {
      setTenants([]);
      toastError(error, t);
    }
  }, [t]);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    // `load` setzt State erst *nach* dem Netzwerk-Await — es entsteht keine
    // kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load, isPlatformAdmin]);

  const formatter = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  /** Mitgliedschaft des Betreibers in diesem Mandanten — `null` = keine. */
  function membershipOf(tenantId: string) {
    return user?.memberships.find((membership) => membership.tenant_id === tenantId) ?? null;
  }

  async function createTenant(event: FormEvent) {
    event.preventDefault();
    setCreateError(null);

    const slug = createForm.slug.trim() || slugFromName(createForm.name);
    const parsed = createTenantSchema.safeParse({
      name: createForm.name.trim(),
      slug,
      settings: { locale: createForm.locale },
    });
    if (!parsed.success) {
      setCreateError(t(createForm.name.trim() === '' ? 'platform.name_required' : 'platform.slug_invalid'));
      return;
    }

    setBusy(true);
    try {
      const created = await platformApi.createTenant(parsed.data);
      setCreating(false);
      setCreateForm(EMPTY_FORM);
      setSlugTouched(false);
      toast.success(t('platform.tenant_created', { name: created.name }));

      // Reihenfolge: erst den aktiven Mandanten setzen, dann navigieren,
      // *dann* die Sitzung neu laden. `reloadSession()` tauscht kurz die
      // ganze Oberflaeche gegen den Ladezustand — nach dem Navigieren stoert
      // das niemanden mehr, davor haette es diese Seite mitten im Klick
      // ausgehaengt.
      setActiveTenant(created.id);
      navigate('/queues');
      await reloadSession();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  async function saveTenant(event: FormEvent, tenantId: string) {
    event.preventDefault();
    setEditError(null);

    const parsed = updateTenantSchema.safeParse({
      name: editForm.name.trim(),
      slug: editForm.slug.trim(),
      settings: { locale: editForm.locale },
    });
    if (!parsed.success) {
      setEditError(t(editForm.name.trim() === '' ? 'platform.name_required' : 'platform.slug_invalid'));
      return;
    }

    setBusy(true);
    try {
      await tenantApi.update(tenantId, parsed.data);
      setEditing(null);
      toast.success(t('platform.tenant_saved'));
      await load();
      // Der Name steht auch in der Mandantenwahl der Kopfzeile.
      await reloadSession();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  /** In den Mandanten wechseln — setzt nur den aktiven Kontext des UI. */
  function openTenant(tenantId: string) {
    setActiveTenant(tenantId);
    navigate('/');
  }

  /**
   * Ein Mandant aus dem Seed (oder von einem anderen Betreiber angelegt) hat
   * keine Mitgliedschaft fuer diesen Menschen — dann fehlt ihm der
   * UI-Kontext, obwohl die API ihn ueberall durchliesse. Ein Klick behebt das
   * ueber den normalen Weg (`POST /members`, mit Audit-Eintrag).
   */
  async function joinTenant(tenantId: string) {
    if (!user) return;
    setBusy(true);
    try {
      await membersApi.add(tenantId, { user_id: user.id, role: 'admin', scopes: [] });
      toast.success(t('platform.joined'));
      await load();
      setActiveTenant(tenantId);
      await reloadSession();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  if (!isPlatformAdmin) {
    return (
      <Alert>
        <AlertDescription>{t('platform.forbidden')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h1 className="text-xl font-semibold">{t('platform.tenants_title')}</h1>
        <p className="text-sm text-muted-foreground">{t('platform.tenants_intro')}</p>
      </section>

      <div>
        <Button onClick={() => setCreating((open) => !open)} disabled={busy}>
          <Plus aria-hidden="true" />
          {t('platform.tenant_create')}
        </Button>
      </div>

      {creating ? (
        <form
          className="grid gap-3 rounded-lg border p-4 sm:grid-cols-3"
          aria-label={t('platform.tenant_create')}
          onSubmit={(event) => void createTenant(event)}
        >
          <div className="space-y-2">
            <Label htmlFor="tenant-name">{t('platform.tenant_name')}</Label>
            <Input
              id="tenant-name"
              value={createForm.name}
              onChange={(event) => {
                const name = event.target.value;
                setCreateForm((form) => ({
                  ...form,
                  name,
                  slug: slugTouched ? form.slug : slugFromName(name),
                }));
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenant-slug">{t('platform.tenant_slug')}</Label>
            <Input
              id="tenant-slug"
              value={createForm.slug}
              onChange={(event) => {
                setSlugTouched(true);
                setCreateForm((form) => ({ ...form, slug: event.target.value }));
              }}
            />
            <p className="text-xs text-muted-foreground">{t('platform.tenant_slug_hint')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenant-locale">{t('platform.tenant_locale')}</Label>
            <Select
              id="tenant-locale"
              value={createForm.locale}
              onChange={(event) =>
                setCreateForm((form) => ({ ...form, locale: event.target.value as Locale }))
              }
            >
              {LOCALES.map((locale) => (
                <option key={locale} value={locale}>
                  {t(`language.${locale}`)}
                </option>
              ))}
            </Select>
          </div>

          {createError ? (
            <p className="text-sm text-destructive sm:col-span-3">{createError}</p>
          ) : null}

          <div className="flex gap-2 sm:col-span-3">
            <Button type="submit" disabled={busy}>
              {t('platform.tenant_create_submit')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setCreating(false);
                setCreateError(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : null}

      {tenants === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : tenants.length === 0 ? (
        <Alert>
          <AlertDescription>{t('platform.tenants_empty')}</AlertDescription>
        </Alert>
      ) : (
        <ul className="space-y-2">
          {tenants.map((tenant) => {
            const membership = membershipOf(tenant.id);
            return (
              <li key={tenant.id} className="space-y-3 rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium">{tenant.name}</span>
                  <code className="rounded bg-muted px-2 py-0.5 text-xs">{tenant.slug}</code>
                  <Badge variant="outline">
                    {t('platform.tenant_members', { count: tenant.member_count })}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {t('platform.tenant_created_at', {
                      date: formatter.format(new Date(tenant.created_at)),
                    })}
                  </span>

                  <div className="ml-auto flex flex-wrap gap-2">
                    {membership ? (
                      <Button size="sm" variant="outline" onClick={() => openTenant(tenant.id)}>
                        {t('platform.tenant_open')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void joinTenant(tenant.id)}
                      >
                        {t('platform.tenant_join')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditError(null);
                        setEditing((current) => (current === tenant.id ? null : tenant.id));
                        setEditForm({
                          name: tenant.name,
                          slug: tenant.slug,
                          locale: tenant.settings.locale,
                        });
                      }}
                    >
                      {t('platform.tenant_edit')}
                    </Button>
                  </div>
                </div>

                {editing === tenant.id ? (
                  <form
                    className="grid gap-3 border-t pt-3 sm:grid-cols-3"
                    aria-label={t('platform.tenant_edit_of', { name: tenant.name })}
                    onSubmit={(event) => void saveTenant(event, tenant.id)}
                  >
                    <div className="space-y-2">
                      <Label htmlFor={`edit-name-${tenant.id}`}>{t('platform.tenant_name')}</Label>
                      <Input
                        id={`edit-name-${tenant.id}`}
                        value={editForm.name}
                        onChange={(event) =>
                          setEditForm((form) => ({ ...form, name: event.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`edit-slug-${tenant.id}`}>{t('platform.tenant_slug')}</Label>
                      <Input
                        id={`edit-slug-${tenant.id}`}
                        value={editForm.slug}
                        onChange={(event) =>
                          setEditForm((form) => ({ ...form, slug: event.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`edit-locale-${tenant.id}`}>
                        {t('platform.tenant_locale')}
                      </Label>
                      <Select
                        id={`edit-locale-${tenant.id}`}
                        value={editForm.locale}
                        onChange={(event) =>
                          setEditForm((form) => ({ ...form, locale: event.target.value as Locale }))
                        }
                      >
                        {LOCALES.map((locale) => (
                          <option key={locale} value={locale}>
                            {t(`language.${locale}`)}
                          </option>
                        ))}
                      </Select>
                    </div>

                    {editError ? (
                      <p className="text-sm text-destructive sm:col-span-3">{editError}</p>
                    ) : null}

                    <div className="flex gap-2 sm:col-span-3">
                      <Button type="submit" size="sm" disabled={busy}>
                        {t('common.save')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => setEditing(null)}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </form>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Alert>
        <AlertDescription>{t('platform.tenant_no_delete')}</AlertDescription>
      </Alert>
    </div>
  );
}
